import * as React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  Alert,
  IconButton,
} from '@mui/material';
import {
  Close as CloseIcon,
  CameraAlt as CameraIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { faceAuthService } from '../services/faceAuthService';
import { useLanguage } from '../contexts/LanguageContext';

interface FaceCaptureDialogProps {
  open: boolean;
  onClose: () => void;
  onCapture: (imageBase64: string) => void;
  title?: string;
  description?: string;
}

export default function FaceCaptureDialog({
  open,
  onClose,
  onCapture,
  title,
  description,
}: FaceCaptureDialogProps) {
  const { t, direction } = useLanguage();
  const videoRef = React.useRef<HTMLVideoElement>(null);
  const streamRef = React.useRef<MediaStream | null>(null);
  const cameraStartedRef = React.useRef(false);

  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [isCameraReady, setIsCameraReady] = React.useState(false);
  const [capturedImage, setCapturedImage] = React.useState<string | null>(null);
  const [videoElementReady, setVideoElementReady] = React.useState(false);

  const isRtl = direction === 'rtl';

  // Reset state when dialog closes
  React.useEffect(() => {
    if (!open) {
      cameraStartedRef.current = false;
      setVideoElementReady(false);
      stopCamera();
    }
  }, [open]);

  // Start camera when dialog opens and video element is ready
  React.useEffect(() => {

    if (open && videoElementReady && !cameraStartedRef.current && videoRef.current) {
      cameraStartedRef.current = true;
      startCamera();
    }

    return () => {
      if (!open) {
        stopCamera();
      }
    };
  }, [open, videoElementReady]);

  const startCamera = async () => {
    setIsLoading(true);
    setError(null);

    // Safety: stop any existing stream before starting a new one
    stopCamera();

    try {
      if (!faceAuthService.isCameraSupported()) {
        throw new Error(t('faceAuth.cameraNotSupported') || 'Camera not supported');
      }

      if (!videoRef.current) {
        throw new Error('Video element not found');
      }

      const stream = await faceAuthService.startCamera(videoRef.current);

      streamRef.current = stream;
      setIsCameraReady(true);
    } catch (err: any) {
      setError(err.message || t('faceAuth.cameraError') || 'Failed to start camera');
    } finally {
      setIsLoading(false);
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      faceAuthService.stopCamera(streamRef.current);
      streamRef.current = null;
    }
    setIsCameraReady(false);
  };

  const handleCapture = () => {
    try {
      if (!videoRef.current) {
        throw new Error('Video element not found');
      }

      const imageBase64 = faceAuthService.captureImageFromVideo(videoRef.current);
      setCapturedImage(imageBase64);
    } catch (err: any) {
      setError(err.message || t('faceAuth.captureError') || 'Failed to capture image');
    }
  };

  const handleRetake = () => {
    stopCamera(); // Stop current stream before resetting state
    cameraStartedRef.current = false;
    setVideoElementReady(false);
    setCapturedImage(null);
  };

  const handleConfirm = () => {
    if (capturedImage) {
      onCapture(capturedImage);
      handleClose();
    }
  };

  const handleClose = () => {
    stopCamera();
    setCapturedImage(null);
    setError(null);
    onClose();
  };

  // Callback ref to detect when video element is mounted
  const videoCallbackRef = React.useCallback((node: HTMLVideoElement | null) => {
    if (node) {
      videoRef.current = node;
      setVideoElementReady(true);
    } else {
      videoRef.current = null;
      setVideoElementReady(false);
    }
  }, []);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          direction: isRtl ? 'rtl' : 'ltr',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pb: 1,
        }}
      >
        <Box component="span" sx={{ fontWeight: 600, fontSize: '1.25rem' }}>
          {title || t('faceAuth.captureTitle') || 'Capture Face'}
        </Box>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {description && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {description}
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Box
          sx={{
            position: 'relative',
            width: '100%',
            paddingTop: '75%', // 4:3 aspect ratio
            backgroundColor: '#000',
            borderRadius: 2,
            overflow: 'hidden',
          }}
        >
          {isLoading && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <CircularProgress />
            </Box>
          )}

          {capturedImage ? (
            <Box
              component="img"
              src={capturedImage}
              alt="Captured face"
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
              }}
            />
          ) : (
            <Box
              component="video"
              ref={videoCallbackRef}
              autoPlay
              playsInline
              muted
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                transform: 'scaleX(-1)', // Mirror the video
              }}
            />
          )}

          {/* Face guide overlay */}
          {!capturedImage && isCameraReady && (
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: '60%',
                paddingTop: '75%',
                border: '3px solid rgba(255, 255, 255, 0.5)',
                borderRadius: '50%',
                pointerEvents: 'none',
              }}
            />
          )}
        </Box>

        {/* Instructions */}
        {!capturedImage && isCameraReady && (
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              {t('faceAuth.instructions') || 'Position your face within the circle and click capture'}
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
        {capturedImage ? (
          <>
            <Button onClick={handleRetake} startIcon={<RefreshIcon />} variant="outlined">
              {t('faceAuth.retake') || 'Retake'}
            </Button>
            <Button onClick={handleConfirm} variant="contained" color="primary">
              {t('faceAuth.confirm') || 'Confirm'}
            </Button>
          </>
        ) : (
          <>
            <Button onClick={handleClose} variant="outlined">
              {t('common.cancel') || 'Cancel'}
            </Button>
            <Button
              onClick={handleCapture}
              variant="contained"
              color="primary"
              disabled={!isCameraReady}
              startIcon={<CameraIcon />}
            >
              {t('faceAuth.capture') || 'Capture'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}

